import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Alert, Modal } from '@mantine/core';
import { useState } from 'react';
import { useDocumentRevisions } from '../../../../api/agents-client/agent-client.hooks';
import { isSafeUrl } from '../../../../helpers/general-helpers';
import { Loader } from '../../../../ui-library/loader/loader';
import { isTextPreviewDocType } from '../../../flora/components/flora-document/flora-document.helpers';
import { PDF_EMBED_PARAMS, useDocumentPreviewFrame } from '../use-document-preview-frame';
import { TextDocumentViewer } from './text-document-viewer';

interface DocPreviewModalProps {
  /** The document to preview, or null when the modal is closed. */
  documentId: string | null;
  /** Display name shown in the modal title. */
  filename?: string;
  /** Revision to open on; the user can then cycle to other revisions. */
  revision?: number;
  /** The document's file-type key; text types render from their text instead of a PDF. */
  docType?: string;
  /** Called when the modal requests to close. */
  onClose: () => void;
}

/**
 * Modal that previews a Flora document (Word/Excel as a PDF; CSV, TSV,
 * Markdown, JSON, or text rendered from its text via
 * {@link TextDocumentViewer}), with
 * prev/next navigation across the document's revisions and a per-revision
 * download. This is the single revision browser reachable from both the grid
 * and the list/table view (the tile itself no longer expands). Reuses
 * {@link useDocumentPreviewFrame} for the poll-until-ready + frame-load guard,
 * mirroring the Google file preview modal's chrome.
 *
 * @param {DocPreviewModalProps} props - The document + initial revision to preview and close handler
 * @returns {JSX.Element} The document preview modal
 */
export function DocPreviewModal({ documentId, filename, revision, docType, onClose }: DocPreviewModalProps) {
  // The revision currently being viewed; seeded from the opening revision and
  // reset (render-phase) whenever the modal opens for a different document.
  const [selectedRevision, setSelectedRevision] = useState<number | undefined>(revision);
  const [trackedDocumentId, setTrackedDocumentId] = useState(documentId);
  if (documentId !== trackedDocumentId) {
    setTrackedDocumentId(documentId);
    setSelectedRevision(revision);
  }

  const { revisions } = useDocumentRevisions(documentId ?? undefined);

  // Revisions come newest-first. Resolve the active revision + its neighbors so
  // the older/newer controls can step through history.
  const effectiveRevision = selectedRevision ?? revisions[0]?.revision;
  const index = revisions.findIndex((rev) => rev.revision === effectiveRevision);
  const olderRevision = index >= 0 && index < revisions.length - 1 ? revisions[index + 1].revision : undefined;
  const newerRevision = index > 0 ? revisions[index - 1].revision : undefined;
  const activeRow = index >= 0 ? revisions[index] : undefined;
  const safeDownloadUrl =
    activeRow?.download_url && isSafeUrl(activeRow.download_url) ? activeRow.download_url : undefined;

  const isTextDocument = isTextPreviewDocType(docType);
  // Text documents skip the PDF conversion, so the frame hook stays idle.
  const { safePreviewUrl, failed, overlayVisible, onFrameLoad, retry } = useDocumentPreviewFrame(
    isTextDocument ? undefined : (documentId ?? undefined),
    effectiveRevision,
  );

  const hasMultipleRevisions = revisions.length > 1;

  return (
    // xOffset 0: the app sizes boxes as content-box, so Mantine's default 5vw
    // side padding would push a wide modal past the right edge.
    <Modal.Root opened={documentId !== null} onClose={onClose} centered radius="lg" size="95%" padding={0} xOffset={0}>
      <Modal.Overlay />
      <Modal.Content>
        <ContentLayout>
          <Header>
            <HeaderLeft>
              <Icon icon="material-symbols:description" width={18} height={18} />
              <Title title={filename}>{filename}</Title>
            </HeaderLeft>
            {hasMultipleRevisions ? (
              <RevNav>
                <NavBtn
                  type="button"
                  onClick={() => olderRevision != null && setSelectedRevision(olderRevision)}
                  disabled={olderRevision == null}
                  aria-label="Older revision"
                >
                  <Icon icon="material-symbols:chevron-left" width={18} height={18} />
                </NavBtn>
                <RevisionTag>{effectiveRevision != null ? `v${effectiveRevision}` : '—'}</RevisionTag>
                <NavBtn
                  type="button"
                  onClick={() => newerRevision != null && setSelectedRevision(newerRevision)}
                  disabled={newerRevision == null}
                  aria-label="Newer revision"
                >
                  <Icon icon="material-symbols:chevron-right" width={18} height={18} />
                </NavBtn>
              </RevNav>
            ) : (
              effectiveRevision != null && <RevisionTag>v{effectiveRevision}</RevisionTag>
            )}
            <HeaderRight>
              {safeDownloadUrl && (
                <OpenLink href={safeDownloadUrl} target="_blank" rel="noopener noreferrer">
                  <Icon icon="material-symbols:download" width={16} height={16} />
                  Download
                </OpenLink>
              )}
              <CloseButton type="button" onClick={onClose} aria-label="Close preview">
                <Icon icon="material-symbols:close" width={18} height={18} />
              </CloseButton>
            </HeaderRight>
          </Header>
          <Body>
            {isTextDocument && documentId && docType && (
              <TextDocumentViewer
                key={documentId}
                documentId={documentId}
                revision={effectiveRevision}
                docType={docType}
              />
            )}
            {!isTextDocument && overlayVisible && (
              <LoadingWrapper>
                <Loader />
              </LoadingWrapper>
            )}
            {!isTextDocument && failed && (
              <ErrorWrapper>
                <Alert color="red" title="Preview unavailable">
                  This document could not be previewed. You can still download it.
                  <RetryButton type="button" onClick={retry}>
                    Try again
                  </RetryButton>
                </Alert>
              </ErrorWrapper>
            )}
            {!isTextDocument && safePreviewUrl && (
              // First-party, presigned PDF rendered by the browser's native
              // viewer (trusted output, no sandbox). Do not point at untrusted
              // content without a security review. `PDF_EMBED_PARAMS` hides the
              // native toolbar (download/print/Drive) — the modal's own Download
              // button remains the escape hatch; `key` stays the bare url so the
              // fragment never remounts the frame.
              <PreviewFrame
                key={safePreviewUrl}
                src={`${safePreviewUrl}${PDF_EMBED_PARAMS}`}
                title={filename ? `Preview of ${filename}` : 'Document preview'}
                referrerPolicy="no-referrer"
                data-testid="doc-preview-modal-iframe"
                onLoad={onFrameLoad}
              />
            )}
          </Body>
        </ContentLayout>
      </Modal.Content>
    </Modal.Root>
  );
}

// Styled as a plain inner wrapper, not via styled(Modal.Content): Mantine also
// applies a Modal.Content className to the modal's flex "inner" wrapper, which
// turned that wrapper into a column and collapsed the 95% width to the
// content's own width.
const ContentLayout = styled.div`
  height: 90vh;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--sky-light);
  color: var(--ink-dark);
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const Title = styled(Modal.Title)`
  font-weight: 700;
  color: var(--ink-darkest);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RevNav = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const NavBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 7px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-light);
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;

  &:hover:not(:disabled) {
    border-color: var(--sky-base);
    background: var(--sky-lighter);
    color: var(--ink-dark);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const RevisionTag = styled.span`
  flex-shrink: 0;
  min-width: 34px;
  text-align: center;
  padding: 2px 7px;
  border-radius: 6px;
  background: var(--secondary-lightest);
  color: var(--secondary-darkest);
  font-size: 12px;
  font-weight: 700;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const OpenLink = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-dark);
  font-size: 13px;
  font-weight: 500;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover {
    border-color: var(--secondary-base);
    background: var(--secondary-lightest);
    color: var(--secondary-darkest);
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--ink-lighter);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: var(--sky-light);
    color: var(--ink-darkest);
  }
`;

const Body = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  background: var(--base-white);
`;

const PreviewFrame = styled.iframe`
  display: block;
  width: 100%;
  height: 100%;
  border: none;
`;

const LoadingWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--base-white);
`;

const ErrorWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 96px;
`;

const RetryButton = styled.button`
  display: block;
  margin-top: 8px;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-dark);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: var(--sky-base);
    background: var(--sky-lighter);
  }
`;
